/**
 * The set of product identifiers a tenant can nominate as its *primary* display
 * code (WB-7419). Kept in its own file so `FeatureFlagService` (which parses the
 * tenant config) and `ProductCodeService` (which resolves it) can both use it
 * without a circular import.
 *
 * Modelled as an enum over the identifier set rather than a boolean "show ERP ID
 * instead of SKU", so the next request ("customer X recognises products by the
 * manufacturer reference") is a config value and not a rebuild.
 */
export type ProductIdentifierKey = 'sku' | 'erp_id' | 'ean';

/** Tenant default. Every other tenant must behave exactly as it did before WB-7419. */
export const DEFAULT_PRODUCT_IDENTIFIER: ProductIdentifierKey = 'sku';

const KNOWN_IDENTIFIERS: readonly ProductIdentifierKey[] = ['sku', 'erp_id', 'ean'];

/**
 * Narrow an untrusted backend value to a known identifier, falling back to SKU.
 *
 * Deliberately defensive: an unrecognised value must never propagate, because a
 * bad config would otherwise blank out (or mislabel) the product code on every
 * screen in the app at once.
 */
export function coerceProductIdentifier(value: unknown): ProductIdentifierKey {
  const key = String(value ?? '').trim().toLowerCase();
  return KNOWN_IDENTIFIERS.includes(key as ProductIdentifierKey)
    ? (key as ProductIdentifierKey)
    : DEFAULT_PRODUCT_IDENTIFIER;
}
